import { Stack, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { Chip } from '@shared/components';
import { ArrowRightIcon } from '@shared/custom-icons';

export type PriceSource =
  | 'markup'
  | 'markupPlusRounding'
  | 'priceSuggestion'
  | 'priceSuggestionPlusRounding'
  | 'campaign'
  | 'customerMarkup';

interface Props {
  currentPriceSource: PriceSource;
  futurePriceSource?: PriceSource;
}

const chipHeight = 28;
const minChipWidth = 80;
const maxChipWidth = 100;

export const LevelChipWithChange = ({ currentPriceSource, futurePriceSource }: Props) => {
  const t = useAppTFunction();
  const theme = useTheme();
  const { tint } = theme.vars.palette.semantic;

  const getLabelAndColor = (source: PriceSource) => {
    switch (source) {
      case 'priceSuggestion':
        return {
          label: t('myAssortment.recommendedPrices.recommendedPricesShort'),
          bg: tint.pink.medium,
          text: tint.pink.text,
        };

      case 'campaign':
        return {
          label: t('myAssortment.campaignSource.short'),
          bg: tint.red.medium,
          text: tint.red.text,
        };

      case 'customerMarkup':
        return {
          label: t('myAssortment.customerMarkupShort'),
          bg: tint.green.medium,
          text: tint.green.text,
        };

      case 'markup':
        return {
          label: t('myAssortment.markupShort'),
          bg: tint.saffron.soft,
          text: tint.saffron.text,
        };
      case 'markupPlusRounding':
        return {
          label: t('myAssortment.markupPlusRoundingShort'),
          bg: tint.sky.strong,
          text: tint.sky.text,
        };
      case 'priceSuggestionPlusRounding':
        return {
          label: t('myAssortment.recommendedPrices.recPricePlusRoundingShort'),
          bg: tint.pink.medium,
          text: tint.pink.text,
        };
    }
  };

  const current = getLabelAndColor(currentPriceSource);
  const future = futurePriceSource ? getLabelAndColor(futurePriceSource) : undefined;

  return future ? (
    <Stack
      flexDirection="row"
      height={chipHeight}
      minWidth={minChipWidth}
      maxWidth={maxChipWidth}
      overflow="hidden"
      borderRadius={2}
    >
      <Stack
        width="50%"
        height={chipHeight}
        justifyContent="center"
        bgcolor={current.bg}
        color={current.text}
        whiteSpace="nowrap"
        textAlign="center"
      >
        {current.label}
      </Stack>
      <Stack
        height={chipHeight}
        width={6}
        justifyContent="center"
        sx={{
          background: `linear-gradient(to right, ${current.bg} 50%, ${future.bg} 50%-100%)`,
        }}
      >
        <ArrowRightIcon />
      </Stack>
      <Stack
        width="50%"
        height={chipHeight}
        justifyContent="center"
        bgcolor={future.bg}
        color={future.text}
        whiteSpace="nowrap"
        textAlign="center"
      >
        {future.label}
      </Stack>
    </Stack>
  ) : (
    <Chip
      sx={{
        height: chipHeight,
        minWidth: minChipWidth,
        maxWidth: maxChipWidth,
        fontSize: '16px',
        borderRadius: currentPriceSource === 'markup' ? '0 !important' : 2,
      }}
      backgroundColor={current.bg}
      textColor={current.text}
      label={current.label}
    />
  );
};
